import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { authService } from "@/services/authService";
import { createLogger } from "@/lib/logger";

const log = createLogger("auth");
const AuthContext = createContext(null);

/**
 * Session state:
 *   status: "loading" | "authenticated" | "unauthenticated"
 *   session: full /me payload (user, workspace, workspace_role, platform_role, permissions, entitlements)
 */
export function AuthProvider({ children }) {
  const [status, setStatus] = useState("loading");
  const [session, setSession] = useState(null);

  const applySession = useCallback((payload) => {
    setSession(payload);
    setStatus("authenticated");
  }, []);

  const refresh = useCallback(async () => {
    try {
      const payload = await authService.me();
      applySession(payload);
      return payload;
    } catch (e) {
      setSession(null);
      setStatus("unauthenticated");
      return null;
    }
  }, [applySession]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const payload = await authService.login(email, password);
    applySession(payload);
    return payload;
  }, [applySession]);

  const register = useCallback(async (form) => {
    const payload = await authService.register(form);
    applySession(payload);
    return payload;
  }, [applySession]);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (e) {
      log.warn("logout request failed", e?.message);
    }
    setSession(null);
    setStatus("unauthenticated");
  }, []);

  const value = useMemo(() => {
    const permissions = session?.permissions || [];
    return {
      status,
      session,
      user: session?.user || null,
      workspace: session?.workspace || null,
      workspaceRole: session?.workspace_role || null,
      platformRole: session?.platform_role || null,
      permissions,
      entitlements: session?.entitlements || [],
      hasPermission: (perm) => permissions.includes(perm),
      isAdmin: permissions.includes("admin.access"),
      login,
      register,
      logout,
      refresh,
    };
  }, [status, session, login, register, logout, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
